import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { recruitmentTheme, themeClasses } from '../styles/theme';
import { 
  Trash2, 
  RefreshCw, 
  AlertTriangle, 
  Users, 
  FileText, 
  Calendar,
  Search,
  Clock,
  Package,
  Briefcase,
  Eye,
  LayoutDashboard,
  CheckSquare
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { 
  getRecycleBinItems, 
  restoreFromRecycleBin, 
  permanentlyDelete as permanentlyDeleteFromBin,
  emptyRecycleBin as emptyBin,
  cleanupRecycleBin,
  RecycleBinItem
} from '../utils/recycleBin';
import { useNotifications } from '../contexts/NotificationContext';
import { 
  getDeletedCandidates, 
  restoreCandidate, 
  permanentlyDeleteCandidate 
} from '../services/candidateService';
import { CandidateWithMeta } from '../types';

const RecycleBinPage: React.FC = () => {
  const [deletedItems, setDeletedItems] = useState<RecycleBinItem[]>([]);
  const [deletedCandidates, setDeletedCandidates] = useState<CandidateWithMeta[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | RecycleBinItem['type']>('all');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { showNotification, notifyContentChange } = useNotifications();

  const loadRecycleBinItems = () => {
    // Auto-cleanup old items
    const cleanedCount = cleanupRecycleBin();
    if (cleanedCount > 0) {
      showNotification({
        type: 'info',
        title: 'Auto-cleanup',
        message: `${cleanedCount} item(s) older than 30 days have been automatically removed.`,
        duration: 5000
      });
    }
    
    const items = getRecycleBinItems();
    const sortedItems = items.sort((a, b) => new Date(b.deletedAt).getTime() - new Date(a.deletedAt).getTime());
    setDeletedItems(sortedItems);
    setLoading(false);
  };

  useEffect(() => {
    console.log('🗑️ RecycleBin: Loading items from localStorage and Firebase...');
    loadRecycleBinItems();
    
    // Setup Firebase real-time listener for deleted candidates
    let unsubscribeFirebase: (() => void) | null = null;
    
    const setupFirebaseSubscription = async () => {
      try {
        unsubscribeFirebase = await getDeletedCandidates((firebaseDeletedCandidates) => {
          console.log('🗑️ RecycleBin: Received', firebaseDeletedCandidates.length, 'deleted candidates from Firebase');
          setDeletedCandidates(firebaseDeletedCandidates);
          setLoading(false);
        }, (error) => {
          console.error('❌ Firebase deleted candidates subscription error:', error);
          setLoading(false);
        });
      } catch (error) {
        console.error('❌ Failed to setup Firebase deleted candidates subscription:', error);
        setLoading(false);
      }
    };

    setupFirebaseSubscription();

    // Handle localStorage changes for other items (tasks, appointments, etc.)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'recycleBin' || event.key === null) {
        console.log('🗑️ RecycleBin: LocalStorage change detected, reloading...');
        loadRecycleBinItems();
      }
    };

    // Add localStorage event listener
    window.addEventListener('storage', handleStorageChange);

    return () => {
      console.log('🗑️ RecycleBin: Cleaning up event listeners...');
      window.removeEventListener('storage', handleStorageChange);
      if (unsubscribeFirebase) {
        unsubscribeFirebase();
      }
    };
  }, []);

  const getItemIcon = (type: RecycleBinItem['type']) => {
    switch (type) {
      case 'candidate': return <Users className="w-4 h-4" />;
      case 'task': return <FileText className="w-4 h-4" />;
      case 'appointment': return <Calendar className="w-4 h-4" />;
      case 'document': return <FileText className="w-4 h-4" />;
      case 'cv': return <FileText className="w-4 h-4" />;
      case 'draft': return <FileText className="w-4 h-4" />;
      case 'job': return <Briefcase className="w-4 h-4" />;
      case 'user': return <Users className="w-4 h-4" />;
      case 'receptionist': return <Eye className="w-4 h-4" />;
      case 'dashboard': return <LayoutDashboard className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  const getItemTypeColor = (type: RecycleBinItem['type']) => {
    switch (type) {
      case 'candidate': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'task': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'appointment': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'document': return 'bg-green-100 text-green-700 border-green-200';
      case 'cv': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'draft': return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'job': return 'bg-teal-100 text-teal-700 border-teal-200';
      case 'user': return 'bg-red-100 text-red-700 border-red-200';
      case 'receptionist': return 'bg-pink-100 text-pink-700 border-pink-200';
      case 'dashboard': return 'bg-cyan-100 text-cyan-700 border-cyan-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const restoreItem = async (itemId: string) => {
    // Check if it's a Firebase candidate first
    const candidate = deletedCandidates.find(c => c.id === itemId || c._id === itemId);
    if (candidate) {
      try {
        await restoreCandidate(candidate.id || candidate._id || itemId);
        showNotification({
          type: 'success',
          title: 'Candidate Restored',
          message: `${candidate.name} has been restored successfully!`,
          duration: 5000
        });
        return;
      } catch (error: any) {
        console.error('Failed to restore candidate:', error);
        showNotification({
          type: 'error',
          title: 'Restore Failed',
          message: 'Failed to restore candidate. Please try again.',
          duration: 5000
        });
        return;
      }
    }

    // Fall back to localStorage items (tasks, appointments, etc.)
    const item = deletedItems.find(i => i.id === itemId);
    if (!item) {
      showNotification({
        type: 'error',
        title: 'Restore Failed',
        message: 'Item not found.',
        duration: 5000
      });
      return;
    }

    try {
      const success = await restoreFromRecycleBin(itemId);
      if (!success) {
        throw new Error('Restore failed');
      }

      showNotification({
        type: 'success',
        title: 'Item Restored',
        message: `${item.name} has been restored successfully!`,
        duration: 5000
      });
      
      // Notify content change
      notifyContentChange('Recycle Bin', 'restored', item.type, item.name);
    } catch (error: any) {
      console.error('Failed to restore item:', error);
      showNotification({
        type: 'error',
        title: 'Restore Failed',
        message: error.message || 'Failed to restore item. Please try again.',
        duration: 5000
      });
    }
  };

  const permanentlyDelete = async (itemId: string) => {
    // Check if it's a Firebase candidate first
    const candidate = deletedCandidates.find(c => c.id === itemId || c._id === itemId);
    if (candidate) {
      if (!window.confirm(`Are you sure you want to permanently delete ${candidate.name}? This action cannot be undone.`)) {
        return;
      }

      try {
        await permanentlyDeleteCandidate(candidate.id || candidate._id || itemId);
        showNotification({
          type: 'success',
          title: 'Candidate Deleted',
          message: `${candidate.name} has been permanently deleted.`,
          duration: 5000
        });
        return;
      } catch (error: any) {
        console.error('Failed to permanently delete candidate:', error);
        showNotification({
          type: 'error',
          title: 'Delete Failed',
          message: 'Failed to permanently delete candidate. Please try again.',
          duration: 5000
        });
        return;
      }
    }

    // Fall back to localStorage items (tasks, appointments, etc.)
    const item = deletedItems.find(i => i.id === itemId);
    if (!item) return;

    if (!window.confirm('Are you sure you want to permanently delete this item? This action cannot be undone.')) {
      return;
    }

    try {
      const success = permanentlyDeleteFromBin(itemId);
      if (!success) {
        throw new Error('Deletion failed');
      }

      showNotification({
        type: 'success',
        title: 'Item Deleted',
        message: 'Item permanently deleted.',
        duration: 5000
      });
      
      // Refresh the items list
      loadRecycleBinItems();
    } catch (error: any) {
      console.error('Failed to delete item:', error);
      showNotification({
        type: 'error',
        title: 'Delete Failed',
        message: 'Failed to delete item. Please try again.',
        duration: 5000
      });
    }
  };

  const emptyRecycleBin = async () => {
    if (!window.confirm('Are you sure you want to permanently delete all items in the recycle bin? This action cannot be undone.')) {
      return;
    }

    try {
      let deletedCount = 0;
      let errors = 0;

      // Delete all Firebase candidates
      for (const candidate of deletedCandidates) {
        try {
          await permanentlyDeleteCandidate(candidate.id || candidate._id || '');
          deletedCount++;
          console.log('✅ Permanently deleted Firebase candidate:', candidate.name);
        } catch (error) {
          console.error('Failed to permanently delete candidate:', error);
          errors++;
        }
      }

      // Empty localStorage recycle bin
      const success = emptyBin();
      if (success) {
        const localStorageItems = deletedItems.length;
        deletedCount += localStorageItems;
        
        showNotification({
          type: 'success',
          title: 'Recycle Bin Emptied',
          message: `All ${deletedCount} item(s) have been permanently deleted.`,
          duration: 5000
        });
        
        // Refresh the items list
        loadRecycleBinItems();
        setSelectedItems([]);
      } else {
        throw new Error('Failed to empty localStorage recycle bin');
      }

      if (errors > 0) {
        showNotification({
          type: 'warning',
          title: 'Partial Success',
          message: `${deletedCount} item(s) deleted successfully, ${errors} failed.`,
          duration: 8000
        });
      }
    } catch (error: any) {
      console.error('Failed to empty recycle bin:', error);
      showNotification({
        type: 'error',
        title: 'Empty Failed',
        message: 'Failed to empty the recycle bin.',
        duration: 5000
      });
    }
  };

  const restoreSelected = async () => {
    let restoredCount = 0;
    let errors = 0;

    for (const itemId of selectedItems) {
      try {
        // Check if it's a Firebase candidate first
        const candidate = deletedCandidates.find(c => c.id === itemId || c._id === itemId);
        if (candidate) {
          try {
            await restoreCandidate(candidate.id || candidate._id || itemId);
            restoredCount++;
            console.log('✅ Restored Firebase candidate:', candidate.name);
            notifyContentChange('Recycle Bin', 'restored', 'candidate', candidate.name);
          } catch (error) {
            console.error('Failed to restore candidate:', error);
            errors++;
          }
        } else {
          // Handle localStorage items (tasks, appointments, etc.)
          const success = await restoreFromRecycleBin(itemId);
          if (success) {
            restoredCount++;
            // Find the item and notify
            const item = deletedItems.find(i => i.id === itemId);
            if (item) {
              notifyContentChange('Recycle Bin', 'restored', item.type, item.name);
            }
          } else {
            errors++;
          }
        }
      } catch (error) {
        errors++;
        console.error('Failed to restore item:', itemId, error);
      }
    }

    setSelectedItems([]);
    
    // Refresh the items list
    loadRecycleBinItems();
    
    if (errors === 0) {
      showNotification({
        type: 'success',
        title: 'Items Restored',
        message: `${restoredCount} item(s) restored successfully.`,
        duration: 5000
      });
    } else {
      showNotification({
        type: 'warning',
        title: 'Partial Success',
        message: `${restoredCount} item(s) restored, ${errors} failed.`,
        duration: 8000
      });
    }
  };

  const deleteSelected = async () => {
    if (!window.confirm(`Are you sure you want to permanently delete ${selectedItems.length} selected items? This action cannot be undone.`)) {
      return;
    }

    let deletedCount = 0;
    let errors = 0;

    for (const itemId of selectedItems) {
      try {
        // Check if it's a Firebase candidate first
        const candidate = deletedCandidates.find(c => c.id === itemId || c._id === itemId);
        if (candidate) {
          try {
            await permanentlyDeleteCandidate(candidate.id || candidate._id || itemId);
            deletedCount++;
            console.log('✅ Permanently deleted Firebase candidate:', candidate.name);
          } catch (error) {
            console.error('Failed to permanently delete candidate:', error);
            errors++;
          }
        } else {
          // Handle localStorage items (tasks, appointments, etc.)
          if (permanentlyDeleteFromBin(itemId)) {
            deletedCount++;
          } else {
            errors++;
          }
        }
      } catch (error) {
        errors++;
        console.error('Failed to delete item:', itemId, error);
      }
    }

    setSelectedItems([]);
    
    // Refresh the items list
    loadRecycleBinItems();
    
    if (errors === 0) {
      showNotification({
        type: 'success',
        title: 'Items Deleted',
        message: `${deletedCount} item(s) permanently deleted.`,
        duration: 5000
      });
    } else {
      showNotification({
        type: 'warning',
        title: 'Partial Success',
        message: `${deletedCount} item(s) deleted, ${errors} failed.`,
        duration: 8000
      });
    }
  };

  // Combine localStorage items and Firebase candidates into a unified list
  const allDeletedItems = [
    ...deletedItems, // localStorage items (tasks, appointments, etc.)
    ...deletedCandidates.map(candidate => ({
      id: candidate.id || candidate._id || '',
      name: candidate.name,
      type: 'candidate' as const,
      details: `Email: ${candidate.email}${candidate.phone ? ', Phone: ' + candidate.phone : ''}`,
      deletedAt: (candidate as any).deletedAt ? 
        ((candidate as any).deletedAt.toDate ? (candidate as any).deletedAt.toDate().toISOString() : (candidate as any).deletedAt) : 
        new Date().toISOString()
    }))
  ];

  const filteredItems = allDeletedItems.filter(item => {
    const matchesSearch = searchTerm === '' || 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.details.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || item.type === filterType;

    return matchesSearch && matchesType;
  });

  const selectAllItems = () => {
    const allItemIds = filteredItems.map(item => item.id);
    setSelectedItems(allItemIds);
  };

  const deselectAllItems = () => {
    setSelectedItems([]);
  };

  const isAllSelected = filteredItems.length > 0 && selectedItems.length === filteredItems.length;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`${themeClasses.pageTitle} ${recruitmentTheme.animations.fadeIn}`}>Recycle Bin</h1>
            <p className="text-gray-600 mt-1">
              Recover recently deleted items. Items are automatically deleted after 30 days.
            </p>
          </div>
        </div>
        <div className={`${recruitmentTheme.components.card} p-8`}>
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8b6914]"></div>
            <span className="ml-3 text-gray-600">Loading recycle bin...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`${themeClasses.pageTitle} ${recruitmentTheme.animations.fadeIn}`}>Recycle Bin</h1>
          <p className="text-gray-600 mt-1">
            Recover recently deleted items. Items are automatically deleted after 30 days.
          </p>
        </div>
      </div>

      <div className={recruitmentTheme.components.card}>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex items-center space-x-2">
              <Trash2 className="w-5 h-5 text-[#8b6914]" />
              <CardTitle className="text-[#8b6914]">Deleted Items</CardTitle>
              {deletedItems.length > 0 && (
                <>
                  <button 
                    onClick={emptyRecycleBin}
                    className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2 text-red-600 hover:text-red-700 ml-4`}
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Empty Recycle Bin</span>
                  </button>
                  <button 
                    onClick={() => {
                      loadRecycleBinItems();
                      showNotification({
                        type: 'info',
                        title: 'Cleanup Complete',
                        message: 'Checked for items older than 30 days.',
                        duration: 3000
                      });
                    }}
                    className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2 ml-2`}
                    title="Manually check for items older than 30 days"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Run Cleanup</span>
                  </button>
                </>
              )}
              <div className="relative ml-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search deleted items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`${recruitmentTheme.components.input} pl-10 w-64`}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                className={recruitmentTheme.components.select}
              >
                <option value="all">All Types</option>
                <option value="candidate">Candidates</option>
                <option value="task">Tasks</option>
                <option value="appointment">Appointments</option>
                <option value="document">Documents</option>
                <option value="cv">CVs</option>
                <option value="draft">CV Drafts</option>
                <option value="job">Jobs</option>
                <option value="user">Users</option>
                <option value="receptionist">Receptionist Items</option>
                <option value="dashboard">Dashboard Items</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredItems.length > 0 && (
            <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <button
                  onClick={isAllSelected ? deselectAllItems : selectAllItems}
                  className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 text-sm`}
                >
                  <CheckSquare className="w-4 h-4" />
                  <span>{isAllSelected ? 'Deselect All' : 'Select All'}</span>
                </button>
                {selectedItems.length > 0 && (
                  <span className="text-sm text-gray-700">
                    {selectedItems.length} of {filteredItems.length} item(s) selected
                  </span>
                )}
              </div>
              {selectedItems.length > 0 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={restoreSelected}
                    className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 text-sm`}
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Restore Selected</span>
                  </button>
                  <button
                    onClick={deleteSelected}
                    className={`${recruitmentTheme.components.button.secondary} text-red-600 hover:text-red-700 flex items-center space-x-1 text-sm`}
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete Permanently</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <Trash2 className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Recycle bin is empty</h3>
              <p className="mt-1 text-sm text-gray-500">
                Deleted items will appear here for 30 days before permanent deletion.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredItems.map((item, index) => (
                <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors">
                  <div className="flex items-center space-x-3 flex-1">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems([...selectedItems, item.id]);
                        } else {
                          setSelectedItems(selectedItems.filter(id => id !== item.id));
                        }
                      }}
                      className="rounded"
                    />
                    <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center">
                      {index + 1}.
                    </span>
                    <div className={`p-2 rounded-lg ${getItemTypeColor(item.type)}`}>
                      {getItemIcon(item.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-gray-900">{item.name}</h4>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getItemTypeColor(item.type)}`}>
                          {item.type}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{item.details}</p>
                      <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                        <span className="flex items-center space-x-1">
                          <Clock className="w-3 h-3" />
                          <span>Deleted {formatDistanceToNow(new Date(item.deletedAt))} ago</span>
                        </span>
                        <span>by {item.deletedBy}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => restoreItem(item.id)}
                      className={`${recruitmentTheme.components.button.ghost} text-blue-600 hover:text-blue-700`}
                      title="Restore item"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => permanentlyDelete(item.id)}
                      className={`${recruitmentTheme.components.button.ghost} text-red-600 hover:text-red-700`}
                      title="Delete permanently"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Auto-deletion Policy</p>
                <p className="text-xs text-yellow-700 mt-1">
                  Items in the recycle bin are automatically deleted after 30 days. 
                  Restore important items before they are permanently removed.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </div>
    </div>
  );
};

export default RecycleBinPage;